import React, { useState } from 'react';
import { Field } from 'react-final-form';
import { func, string, object, bool, number } from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import {
  LocationAutocompleteInputField,
  FieldSingleDatePicker,
  InlineTextButton,
  Icon,
} from '../../components';

import css from './TourScheduleField.module.css';

const identity = v => v;

const TourScheduleField = props => {
  const { name, intl, onRemove, canRemove, index, form, autoFocus } = props;

  // State for collapsed/expanded - expand if it's a new field (autoFocus is true)
  const [isCollapsed, setIsCollapsed] = useState(!autoFocus);

  const locationLabel = intl.formatMessage({
    id: 'EditListingTouringLinksForm.tourLocationLabel',
  });
  const locationPlaceholder = intl.formatMessage({
    id: 'EditListingTouringLinksForm.tourLocationPlaceholder',
  });
  const startDateLabel = intl.formatMessage({
    id: 'EditListingTouringLinksForm.tourStartDateLabel',
  });
  const endDateLabel = intl.formatMessage({
    id: 'EditListingTouringLinksForm.tourEndDateLabel',
  });
  const removeTourLabel = intl.formatMessage({
    id: 'EditListingTouringLinksForm.removeTourLabel',
  });

  const locationRequired = validators.autocompleteSearchRequired(
    intl.formatMessage({
      id: 'EditListingTouringLinksForm.tourLocationRequired',
    })
  );
  const locationSelected = validators.autocompletePlaceSelected(
    intl.formatMessage({
      id: 'EditListingTouringLinksForm.tourLocationSelected',
    })
  );
  const composeLocationValidators = validators.composeValidators(
    locationRequired,
    locationSelected
  );

  const startDateRequired = validators.required(
    intl.formatMessage({
      id: 'EditListingTouringLinksForm.tourStartDateRequired',
    })
  );
  const endDateRequired = validators.required(
    intl.formatMessage({
      id: 'EditListingTouringLinksForm.tourEndDateRequired',
    })
  );

  // Function to check if end date is after start date
  const endDateAfterStart = (value, allValues) => {
    const startDate = allValues?.tourSchedules?.[index]?.startDate?.date;
    const endDate = value?.date;

    if (startDate && endDate && endDate <= startDate) {
      return intl.formatMessage({
        id: 'EditListingTouringLinksForm.tourEndDateAfterStart',
      });
    }
    return undefined;
  };

  const composeEndDateValidators = validators.composeValidators(endDateRequired, endDateAfterStart);

  // Function to check if date is in the future
  const futureDateOnly = value => {
    const date = value?.date;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date && date < today) {
      return intl.formatMessage({
        id: 'EditListingTouringLinksForm.tourFutureDateOnly',
      });
    }
    return undefined;
  };

  const composeStartDateValidators = validators.composeValidators(
    startDateRequired,
    futureDateOnly
  );

  // Format date for display
  const formatDate = dateValue => {
    if (!dateValue || !dateValue.date) return 'N/A';
    const date = new Date(dateValue.date);
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Get the date range display
  const startDate = form.values?.tourSchedules?.[index]?.startDate;
  const endDate = form.values?.tourSchedules?.[index]?.endDate;
  const dateRangeDisplay = `${formatDate(startDate)} - ${formatDate(endDate)}`;

  return (
    <div className={css.tourScheduleItem}>
      <div className={css.tourScheduleHeader}>
        <InlineTextButton
          type="button"
          className={css.tourScheduleTitleContainer}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <h3 className={css.tourScheduleTitle}>{dateRangeDisplay}</h3>
          <Icon
            name={isCollapsed ? 'chevronDown' : 'chevronUp'}
            size={12}
            className={css.arrowIcon}
          />
        </InlineTextButton>
        {canRemove && (
          <InlineTextButton type="button" className={css.removeTourButton} onClick={onRemove}>
            <Icon name="close" size={16} className={css.removeIcon} />
            {removeTourLabel}
          </InlineTextButton>
        )}
      </div>

      {!isCollapsed && (
        <div className={css.tourScheduleFields}>
          <LocationAutocompleteInputField
            rootClassName={css.locationField}
            inputClassName={css.locationAutocompleteInput}
            iconClassName={css.locationAutocompleteInputIcon}
            predictionsClassName={css.predictionsRoot}
            name={`${name}.location`}
            label={locationLabel}
            placeholder={locationPlaceholder}
            useDefaultPredictions={false}
            format={identity}
            valueFromForm={form.values?.tourSchedules?.[index]?.location}
            validate={composeLocationValidators}
            autoFocus={autoFocus}
          />

          <div className={css.dateFields}>
            <FieldSingleDatePicker
              className={css.dateField}
              name={`${name}.startDate`}
              id={`${name}.startDate`}
              label={startDateLabel}
              validate={composeStartDateValidators}
              placeholderText={intl.formatMessage({
                id: 'EditListingTouringLinksForm.tourStartDatePlaceholder',
              })}
              isOutsideRange={() => false}
            />

            <FieldSingleDatePicker
              className={css.dateField}
              name={`${name}.endDate`}
              id={`${name}.endDate`}
              label={endDateLabel}
              validate={composeEndDateValidators}
              placeholderText={intl.formatMessage({
                id: 'EditListingTouringLinksForm.tourEndDatePlaceholder',
              })}
              isOutsideRange={() => false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

TourScheduleField.propTypes = {
  name: string.isRequired,
  intl: intlShape.isRequired,
  onRemove: func,
  canRemove: bool,
  index: number.isRequired,
  form: object,
  autoFocus: bool,
};

export default injectIntl(TourScheduleField);
